import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  BarChart3,
  Save,
  CheckCircle2,
  Users,
  Award,
  ShieldCheck,
  HeartHandshake,
  Star,
  Zap,
  TrendingUp,
  Headphones,
} from 'lucide-react';

const iconMap: Record<string, any> = {
  Users,
  Award,
  ShieldCheck,
  HeartHandshake,
  Star,
  Zap,
  TrendingUp,
  Headphones,
};

export default function QuickStatsManager() {
  const { settings, updateSettingsOnServer, showToast } = useApp();

  const [quickStats, setQuickStats] = useState(
    settings?.quickStats || {
      items: [
        {
          id: 'qs-1',
          icon: 'Users',
          value: '150+',
          label: 'Partner Enterprises',
          desc: 'Trusted by leading corporations, energy firms & B2B institutions',
        },
        {
          id: 'qs-2',
          icon: 'Award',
          value: '98.5%',
          label: 'ESG Transition Success',
          desc: 'Proven track record in corporate sustainability milestones',
        },
        {
          id: 'qs-3',
          icon: 'ShieldCheck',
          value: '100%',
          label: 'ISO 14064 Compliance',
          desc: 'Grounded in accredited international carbon accounting standards',
        },
        {
          id: 'qs-4',
          icon: 'HeartHandshake',
          value: 'ITB Research',
          label: 'Academic-Industry Bridge',
          desc: 'Connecting top academic research with real-world enterprise needs',
        },
      ],
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);

  const handleUpdateItem = (index: number, key: string, val: string) => {
    const updated = [...quickStats.items];
    updated[index] = { ...updated[index], [key]: val };
    setQuickStats({ ...quickStats, items: updated });
  };

  const handleSave = async () => {
    if (!settings) return;
    setIsSaving(true);
    try {
      await updateSettingsOnServer({
        ...settings,
        quickStats,
      });
      const timeStr = new Date().toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      setLastSavedTime(timeStr);
      showToast('Pengaturan 4 Kartu Angka Capaian (Quick Stats) berhasil disimpan!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Gagal menyimpan perubahan Quick Stats', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const primaryColor = settings?.theme?.primaryColor || '#1B4332';

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Header & Save Button */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-bold mb-2 border border-amber-200">
            <BarChart3 className="w-3.5 h-3.5 text-amber-600" />
            <span>Bagian dari Grid Tata Letak Beranda</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            Pengaturan 4 Kartu Angka Capaian (Quick Stats)
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Ubah 4 kartu angka pencapaian perusahaan, mitra enterprise, dan kredensial utama yang tampil di beranda.
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 rounded-xl text-white font-bold text-xs sm:text-sm shadow-md flex items-center gap-2 transition-all cursor-pointer hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: primaryColor }}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Simpan Angka Capaian</span>
              </>
            )}
          </button>

          {lastSavedTime && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-50 border border-emerald-300 rounded-lg text-emerald-800 text-[11px] font-bold">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span>Tersimpan ({lastSavedTime})</span>
            </div>
          )}
        </div>
      </div>

      {/* Live Preview of 4 Cards */}
      <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3">
        <span className="text-[11px] font-black uppercase tracking-wider text-slate-600">
          Pratinjau Tampilan Live di Beranda:
        </span>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          {quickStats.items.map((stat, idx) => {
            const IconComponent = iconMap[stat.icon] || BarChart3;
            return (
              <div
                key={stat.id || idx}
                className="p-4 rounded-xl bg-white border border-slate-200/80 shadow-2xs hover:shadow-xs transition-shadow"
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white mb-2.5 shadow-3xs"
                  style={{ backgroundColor: primaryColor }}
                >
                  <IconComponent className="w-4 h-4" />
                </div>
                <div className="text-xl font-black text-slate-900 tracking-tight">{stat.value || '0'}</div>
                <div className="text-xs font-bold text-slate-800 mt-0.5">{stat.label || 'Judul'}</div>
                <div className="text-[11px] text-slate-500 mt-1 leading-snug line-clamp-2">{stat.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 4 Cards Editor Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quickStats.items.map((stat, idx) => (
          <div
            key={stat.id || idx}
            className="p-5 rounded-2xl border border-slate-200 bg-white shadow-2xs space-y-4"
          >
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-amber-100 text-amber-900 font-bold text-xs flex items-center justify-center">
                  #{idx + 1}
                </span>
                <span className="text-xs font-black text-slate-800 uppercase">
                  Kartu Statistik #{idx + 1}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] font-bold text-slate-500">Ikon:</label>
                <select
                  value={stat.icon}
                  onChange={(e) => handleUpdateItem(idx, 'icon', e.target.value)}
                  className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-slate-50 font-bold text-slate-800 focus:bg-white focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="Users">👥 Users (Mitra & Tim)</option>
                  <option value="Award">🏆 Award (Prestasi & Kepuasan)</option>
                  <option value="ShieldCheck">🛡️ ShieldCheck (Kepatuhan / Standar)</option>
                  <option value="HeartHandshake">🤝 HeartHandshake (Kemitraan)</option>
                  <option value="Zap">⚡ Zap (Energi / Kecepatan)</option>
                  <option value="TrendingUp">📈 TrendingUp (Pertumbuhan)</option>
                  <option value="Headphones">🎧 Headphones (Layanan & Dukungan)</option>
                  <option value="Star">⭐ Star (Bintang Unggulan)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  Angka / Nilai
                </label>
                <input
                  type="text"
                  value={stat.value}
                  onChange={(e) => handleUpdateItem(idx, 'value', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm font-black text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="150+"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  Judul Label Kartu
                </label>
                <input
                  type="text"
                  value={stat.label}
                  onChange={(e) => handleUpdateItem(idx, 'label', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="Partner Enterprises"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">
                Deskripsi Singkat / Keterangan
              </label>
              <textarea
                rows={2}
                value={stat.desc}
                onChange={(e) => handleUpdateItem(idx, 'desc', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-700 focus:bg-white focus:ring-2 focus:ring-emerald-500"
                placeholder="Penjelasan singkat mengenai angka pencapaian..."
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
